import { useTranslation } from "react-i18next";
import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Share2 } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { fmtDuration } from "@/lib/montageApi";
import { musicDownloadUrl, musicVideoUrl, type MusicJob } from "@/lib/musicApi";

const fileNameFor = (job: MusicJob) =>
  `${(job.image_name.replace(/\.[^.]+$/, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")) || "music"}.mp4`;

// Result card for a READY Music job: preview + download + share (same Web Share flow as Montage renders).
export function MusicResult({ job }: { job: MusicJob }) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const fileName = fileNameFor(job);

  const fetchBlob = async () => {
    const resp = await fetch(musicDownloadUrl(job.id), { credentials: "include" });
    if (!resp.ok) throw new Error();
    return resp.blob();
  };

  const download = async () => {
    setDownloading(true);
    try {
      const url = URL.createObjectURL(await fetchBlob());
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("music.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const share = async () => {
    if (sharing) return;
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (typeof nav.share !== "function" || typeof nav.canShare !== "function" ||
        !nav.canShare({ files: [new File([], fileName, { type: "video/mp4" })] })) {
      toast.info(t("render.shareUnavailable"));
      return;
    }
    setSharing(true);
    try {
      const file = new File([await fetchBlob()], fileName, { type: "video/mp4" });
      if (!nav.canShare({ files: [file] })) {
        toast.info(t("render.shareUnavailable"));
        return;
      }
      await nav.share({ files: [file], title: fileName });
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      toast.error(t("render.shareFailed"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div data-testid="music-result" className="space-y-4">
      <video
        key={job.id}
        data-testid="music-result-video"
        src={`${musicVideoUrl(job.id)}?v=${job.id}`}
        controls
        className="max-h-[420px] w-full rounded-xl border border-[#FF3DBE]/25 bg-black"
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {t("music.duration")}: <span data-testid="music-result-duration" className="text-foreground">{fmtDuration(job.duration)}</span>
          <span className="mx-2 opacity-40">·</span>{job.aspect_ratio}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="music-download-btn" variant="secondary" size="sm" onClick={download} disabled={downloading}>
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {t("common.downloadMp4")}
          </Button>
          <Button data-testid="music-share-btn" variant="secondary" size="sm" onClick={share} disabled={sharing}>
            {sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
            {t("render.share")}
          </Button>
        </div>
      </div>
    </div>
  );
}
